import { motion } from "framer-motion";

/**
 * Auto-playing "building being drawn" animation.
 * Triggers once when scrolled into view. Uses framer-motion whileInView.
 */
const EASE = [0.22, 1, 0.36, 1];
const S = 0.6;
const inView = { once: true, margin: "-20% 0px" };

// Reusable animation factory
const draw = (delay, dur = 0.9) => ({
    initial: { pathLength: 0, opacity: 0 },
    whileInView: { pathLength: 1, opacity: 1 },
    viewport: inView,
    transition: { pathLength: { duration: dur, ease: EASE, delay }, opacity: { duration: 0.3, delay } },
});

const fade = (delay, dur = 0.7) => ({
    initial: { opacity: 0 },
    whileInView: { opacity: 1 },
    viewport: inView,
    transition: { duration: dur, ease: EASE, delay },
});

const rectDraw = (delay, dur = 0.7) => ({
    initial: { opacity: 0, scale: 0.8 },
    whileInView: { opacity: 1, scale: 1 },
    viewport: inView,
    transition: { duration: dur, ease: EASE, delay },
});

export default function BuildingLineDraw() {
    return (
        <section
            data-testid="building-line-draw"
            className="relative bg-forest-900 text-ivory py-24 md:py-40 overflow-hidden"
        >
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 md:mb-20">
                <div className="md:col-span-6">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={inView}
                        transition={{ duration: 0.8, ease: EASE }}
                        className="flex items-center gap-4 mb-6"
                    >
                        <span className="h-px w-10 bg-lime" />
                        <span className="font-mono-mini text-[10px] tracking-[0.42em] uppercase text-lime">
                            From line to landmark · A KLiving process
                        </span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={inView}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
                        className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-[14ch]"
                    >
                        Every project begins on a <em className="italic text-lime">single line.</em>
                    </motion.h2>
                </div>
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={inView}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
                    className="md:col-span-4 md:col-start-9 text-ivory/70 leading-relaxed"
                >
                    Watch a KLiving residence take shape in real time — from the first ground line, through columns and slabs, up to the roof crown.
                </motion.div>
            </div>

            <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                {/* Blueprint canvas */}
                <div className="md:col-span-8 relative aspect-[16/11] bg-forest-950 border border-lime/15 overflow-hidden">
                    <svg
                        viewBox="0 0 800 550"
                        className="absolute inset-0 h-full w-full"
                        preserveAspectRatio="xMidYMid meet"
                    >
                        <defs>
                            <pattern id="grid-sm-2" width="40" height="40" patternUnits="userSpaceOnUse">
                                <path d="M40 0 L0 0 0 40" fill="none" stroke="rgba(212,255,46,0.08)" strokeWidth="0.5" />
                            </pattern>
                            <pattern id="grid-lg-2" width="200" height="200" patternUnits="userSpaceOnUse">
                                <path d="M200 0 L0 0 0 200" fill="none" stroke="rgba(212,255,46,0.15)" strokeWidth="0.6" />
                            </pattern>
                        </defs>
                        <rect width="800" height="550" fill="url(#grid-sm-2)" />
                        <rect width="800" height="550" fill="url(#grid-lg-2)" />

                        <g stroke="rgba(212,255,46,0.35)" strokeWidth="1" fill="none">
                            <path d="M20 20 L 40 20 M20 20 L20 40" />
                            <path d="M780 20 L760 20 M780 20 L780 40" />
                            <path d="M20 530 L40 530 M20 530 L20 510" />
                            <path d="M780 530 L760 530 M780 530 L780 510" />
                        </g>

                        {/* Ground */}
                        <motion.line x1="60" y1="470" x2="740" y2="470" stroke="#d4ff2e" strokeWidth="1.5" {...draw(0 * S, 1)} />

                        {/* Foundation */}
                        <motion.rect x="180" y="460" width="440" height="18" fill="none" stroke="#d4ff2e" strokeWidth="1.2" {...draw(0.5 * S, 0.9)} />

                        {/* Columns */}
                        {[210, 335, 465, 590].map((x, i) => (
                            <motion.line
                                key={x}
                                x1={x} y1="460" x2={x} y2="140"
                                stroke="rgba(245,245,240,0.9)" strokeWidth="1.2"
                                {...draw(0.9 * S + i * 0.08, 1)}
                            />
                        ))}

                        {/* Slabs */}
                        {[390, 330, 270, 210, 150].map((y, i) => (
                            <motion.line
                                key={y}
                                x1="200" y1={y} x2="600" y2={y}
                                stroke="rgba(245,245,240,0.9)" strokeWidth="1.2"
                                {...draw(1.5 * S + i * 0.1, 0.7)}
                            />
                        ))}

                        {/* Roof */}
                        <motion.polyline
                            points="180,140 400,80 620,140"
                            fill="none" stroke="rgba(245,245,240,0.95)" strokeWidth="1.4"
                            {...draw(2.4 * S, 0.9)}
                        />

                        {/* Windows */}
                        <motion.g {...fade(2.9 * S)}>
                            {[235, 297, 360, 422, 485, 548].map((x) => (
                                <rect key={x} x={x} y={350} width="30" height="26" fill="rgba(212,255,46,0.14)" stroke="rgba(212,255,46,0.5)" strokeWidth="0.8" />
                            ))}
                        </motion.g>
                        <motion.g {...fade(3.1 * S)}>
                            {[235, 297, 360, 422, 485, 548].map((x) => (
                                <rect key={x} x={x} y={290} width="30" height="26" fill="rgba(212,255,46,0.14)" stroke="rgba(212,255,46,0.5)" strokeWidth="0.8" />
                            ))}
                        </motion.g>
                        <motion.g {...fade(3.3 * S)}>
                            {[[235,230],[297,230],[360,230],[422,230],[485,230],[548,230],[235,170],[297,170],[360,170],[422,170],[485,170],[548,170]].map(([x,y],i) => (
                                <rect key={i} x={x} y={y} width="30" height="26" fill="rgba(212,255,46,0.14)" stroke="rgba(212,255,46,0.5)" strokeWidth="0.8" />
                            ))}
                        </motion.g>

                        {/* Roof crown */}
                        <motion.rect x="382" y="52" width="36" height="28" fill="#d4ff2e" {...rectDraw(3.6 * S)} />

                        {/* Context: trees + person */}
                        <motion.g {...fade(3.9 * S)} stroke="rgba(245,245,240,0.55)" strokeWidth="1" fill="none">
                            <circle cx="105" cy="440" r="22" />
                            <line x1="105" y1="462" x2="105" y2="490" />
                            <circle cx="80" cy="450" r="14" />
                            <line x1="80" y1="464" x2="80" y2="490" />
                            <circle cx="700" cy="440" r="22" />
                            <line x1="700" y1="462" x2="700" y2="490" />
                            <circle cx="150" cy="450" r="4" />
                            <line x1="150" y1="454" x2="150" y2="472" />
                            <line x1="150" y1="460" x2="145" y2="470" />
                            <line x1="150" y1="460" x2="155" y2="470" />
                            <line x1="150" y1="472" x2="145" y2="482" />
                            <line x1="150" y1="472" x2="155" y2="482" />
                        </motion.g>

                        <g fontFamily="JetBrains Mono, monospace" fontSize="9" fill="rgba(212,255,46,0.65)" letterSpacing="0.15em">
                            <text x="60" y="510">GL — 0.00 m</text>
                            <text x="640" y="510">S1:1000</text>
                            <text x="60" y="52">KL · DWG 001</text>
                        </g>
                    </svg>

                    {/* Live building label */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={inView}
                        transition={{ duration: 0.8, ease: EASE, delay: 3.9 * S }}
                        className="absolute bottom-4 left-4 flex items-center gap-3 font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-lime opacity-75 animate-ping" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-lime" />
                        </span>
                        <span>Complete — the KLiving residence</span>
                    </motion.div>
                </div>

                {/* Step list */}
                <ol className="md:col-span-4 space-y-4 md:space-y-5">
                    {[
                        { s: "01", t: "Ground line", body: "The site survey and 0.00m datum.", d: 0 * S },
                        { s: "02", t: "Foundation & columns", body: "Raft, footings, load-bearing frame.", d: 0.6 * S },
                        { s: "03", t: "Slabs, floor by floor", body: "Post-tensioned floor plates.", d: 1.5 * S },
                        { s: "04", t: "Envelope & openings", body: "Double-glazed apertures, deep loggias.", d: 2.9 * S },
                        { s: "05", t: "Roof crown", body: "The signature — one lime block.", d: 3.6 * S },
                    ].map((step) => (
                        <motion.li
                            key={step.s}
                            initial={{ opacity: 0.3, x: -8 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={inView}
                            transition={{ duration: 0.6, ease: EASE, delay: step.d }}
                            data-testid={`build-step-${step.s}`}
                            className="grid grid-cols-6 items-baseline gap-3 pb-4 border-b border-white/10"
                        >
                            <div className="col-span-1 font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime">
                                {step.s}
                            </div>
                            <div className="col-span-5">
                                <div className="font-serif-display text-xl md:text-2xl text-ivory leading-tight">
                                    {step.t}
                                </div>
                                <div className="mt-1 text-ivory/60 text-sm">{step.body}</div>
                                <motion.div
                                    initial={{ scaleX: 0 }}
                                    whileInView={{ scaleX: 1 }}
                                    viewport={inView}
                                    transition={{ duration: 0.7, ease: EASE, delay: step.d + 0.2 }}
                                    style={{ transformOrigin: "left" }}
                                    className="h-[2px] bg-lime mt-3"
                                />
                            </div>
                        </motion.li>
                    ))}
                </ol>
            </div>
        </section>
    );
}
